import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  List, Button, Avatar, Typography, message, Spin,
  Modal, Radio, Divider, Tabs
} from "antd";
import { useNavigate } from "react-router-dom";
import {
  DeleteOutlined,
  DownloadOutlined,
  ShoppingCartOutlined
} from "@ant-design/icons";
import { getCardInfo } from "../api_v2/subscription";
import { submitAndPollTrackPurchase } from "../api_v2/mediaTrackPurchase";

const { Title }   = Typography;
const { TabPane } = Tabs;

/* ───── константы ───── */
const LS_KEY         = "simolapps_state";
const GENRE_QURAN    = "коран";
const CHEAP_GENRES   = ["нашиды", "нашиды без музыки", "мавлиды"];

/* базовая (оптовая) цена */
const PRICE_DEFAULT_BASE = 1.5;   // обычные жанры
const PRICE_CHEAP_BASE   = 3;     // «дешёвые» жанры

/* коэффициент наценки в зависимости от количества */
const coefByCount = (n) => {
  if (n > 100) return 1.00;
  if (n >  50) return 1.25;   //  51-100
  if (n >  30) return 1.35;   //  31-50
  if (n >  20) return 1.50;   //  21-30
  if (n >  10) return 1.75;   //  11-20
  return               2.00;  //   1-10
};

/* ───── вспомогалки ───── */
const basePrice = (g) =>
  CHEAP_GENRES.includes(g) ? PRICE_CHEAP_BASE : PRICE_DEFAULT_BASE;

const amountForList = (list) =>
  Math.floor(                               // копейки не выводим
    list.reduce((s, t) => s + basePrice((t.genre || "").toLowerCase()), 0) *
    coefByCount(list.length)
  );

/* ───── компонент ───── */
export default function DownloadQueuePage() {
  /* ---------- state ---------- */
  const [freeTracks, setFreeTracks] = useState([]);   // Коран
  const [paidTracks, setPaidTracks] = useState([]);   // всё, кроме Корана
  const [purchased,  setPurchased]  = useState([]);   // id купленных
  const [methods,    setMethods]    = useState([]);
  const [loading,    setLoading]    = useState(true);

  /* UI-состояние */
  const [freeM,  setFreeM]  = useState(false);
  const [payM,   setPayM]   = useState(false);
  const [zipM,   setZipM]   = useState(false);        // модалка «Скачать купленные»

  const [zipSt,  setZipSt]  = useState("idle");       // idle | preparing | ready
  const [zipUrl, setZipUrl] = useState(null);

  const [groupBy, setGroupBy] = useState("none");
  const [payOpt,  setPayOpt]  = useState("new");
  const checkoutControllerRef = useRef(null);

  /* ---------- user / nav ---------- */
  const user = useMemo(() => {
    try { return JSON.parse(localStorage.getItem(LS_KEY)).user; }
    catch { return null; }
  }, []);
  const navigate = useNavigate();

  /* ---------- загрузка ---------- */
  useEffect(() => {
    if (!user) return;

    const fetchJSON = (url, body) =>
      fetch(url, {
        method : "POST",
        headers: { "Content-Type":"application/json" },
        body   : JSON.stringify(body)
      }).then(r => r.json());

    (async () => {
      try {
        /* 1 ▸ очередь */
        const q = await fetch(
          `https://simolmedia.tw1.ru/api/get_download_queue.php?user_id=${user.id}`
        ).then(r => r.json());
        if (!q.success) throw Error("queue");

        /* 2 ▸ треки (если API вернул quran/paid – берём напрямую) */
        if (Array.isArray(q.quran) && Array.isArray(q.paid)) {
          setFreeTracks(q.quran);
          setPaidTracks(q.paid);
        } else {
          const ids = q.track_ids || [];
          const tr  = await fetchJSON(
            "https://simolmedia.tw1.ru/api/get_tracks_by_ids.php",
            { track_ids: ids }
          );
          if (!tr.success) throw Error("tracks");
          setFreeTracks(tr.tracks.filter(
            t => (t.genre || "").toLowerCase() === GENRE_QURAN
          ));
          setPaidTracks(tr.tracks.filter(
            t => (t.genre || "").toLowerCase() !== GENRE_QURAN
          ));
        }

        /* 3 ▸ купленные */
        const p = await fetchJSON(
          "https://simolmedia.tw1.ru/api/get_purchased_tracks.php",
          { user_id: user.id }
        );
        if (p.success) setPurchased(p.track_ids.map(Number));

        /* 4 ▸ сохранённые карты */
        const m = await getCardInfo();
        if (m.success && Array.isArray(m.cards)) {
          setMethods(m.cards);
          if (m.cards.length > 0) setPayOpt("saved");
        }

      } catch (e) {
        console.error(e);
        message.error("Не удалось загрузить корзину");
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  useEffect(() => () => checkoutControllerRef.current?.abort(), []);

  /* ---------- разбор ---------- */
  const paidNew   = paidTracks.filter(t => !purchased.includes(t.id));
  const paidOwned = paidTracks.filter(t =>  purchased.includes(t.id));

  /* ---------- удалить из корзины ---------- */
  const removeFromQueue = async (trackId) => {
    try {
      const r = await fetch(
        "https://simolmedia.tw1.ru/api/remove_from_download_queue.php",
        {
          method : "POST",
          headers: { "Content-Type":"application/json" },
          body   : JSON.stringify({ user_id:user.id, track_ids:[trackId] })
        }
      ).then(x=>x.json());

      if (r.success){
        setFreeTracks( arr => arr.filter(t => t.id !== trackId));
        setPaidTracks( arr => arr.filter(t => t.id !== trackId));
        message.success("Удалено");
      } else {
        message.error(r.message || "Не удалось удалить");
      }
    } catch { message.error("Ошибка сети"); }
  };

  /* ---------- подготовка / скачивание ZIP ---------- */
  const prepareZip = async (ids, isFree) => {
    setZipSt("preparing"); setZipUrl(null);
    const ep = isFree
      ? "https://simolmedia.tw1.ru/api/download_zip_free.php"
      : "https://simolmedia.tw1.ru/api/download_zip_paid.php";

    try{
      const r = await fetch(ep,{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({ user_id:user.id, track_ids:ids, group_by:groupBy })
      }).then(x=>x.json());
      if(r.success && r.url){ setZipUrl(r.url); setZipSt("ready"); }
      else { message.error("Ошибка подготовки архива"); setZipSt("idle"); }
    }catch{ message.error("Ошибка сети"); setZipSt("idle"); }
  };

  const startDownload = () => {
    const a = document.createElement("a");
    a.href = zipUrl;
    a.download = "tracks.zip";
    a.click();
    setZipSt("idle"); setZipUrl(null);
    setFreeM(false); setPayM(false); setZipM(false);
  };

  /* ---------- оплата ---------- */
  const pay = async () => {
    if (paidNew.length === 0) return;
    checkoutControllerRef.current?.abort();
    const controller = new AbortController();
    checkoutControllerRef.current = controller;
    setZipSt("preparing");

    try{
      const result = await submitAndPollTrackPurchase(
        paidNew.map(t => Number(t.id)),
        groupBy,
        payOpt === "saved" ? "saved" : "new",
        { signal: controller.signal }
      );
      if (result.state === "succeeded"){
        message.success("Оплата прошла");
        prepareZip(paidNew.map(t=>t.id), false);
      } else if (result.state === "redirect"){
        window.location.href = result.paymentUrl;
      } else {
        setZipSt("idle");
        message.info(
          result.executionEnabled
            ? "Платёж продолжает готовиться. Повторите позже."
            : "Оплата треков пока безопасно выключена до переноса каталога и webhook."
        );
      }
    }catch(error){
      if (error?.name === "AbortError" || error?.code === "ERR_CANCELED") return;
      setZipSt("idle");
      message.error(error?.response?.data?.message || error?.message || "Ошибка оплаты");
    }
  };

  /* ---------- UI ---------- */
  if (loading) return <Spin style={{ margin: 60 }} />;

  return (
    <div style={{ padding: 20 }}>
      {/* Шапка */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <Title level={3} style={{ margin:0 }}>Корзина загрузок</Title>
        <Button type="text" size="large" onClick={()=>navigate(-1)}>✕ Закрыть</Button>
      </div>

      {/* ↘︎ три вкладки: 1-не купленные, 2-купленные, 3-Коран */}
      <Tabs defaultActiveKey="new" style={{ marginTop: 24 }}>
        {/* ❶ Не купленные */}
        <TabPane tab={`Не купленные (${paidNew.length})`} key="new">
          {paidNew.length === 0
            ? <p>Пусто</p>
            : (
              <List
                dataSource={paidNew}
                renderItem={t=>(
                  <List.Item
                    actions={[
                      <Button
                        type="link"
                        icon={<DeleteOutlined/>}
                        onClick={()=>removeFromQueue(t.id)}
                      />
                    ]}
                  >
                    <List.Item.Meta
                      avatar={<Avatar src={
                        t.track_cover_photo
                          ? `https://audiobuzz-web.tmweb.ru/uploads/thumbnails/media/${t.track_cover_photo}`
                          : "https://audiobuzz-web.tmweb.ru/uploads/thumbnails/default_cover.jpg"
                      }/>}
                      title={t.title}
                      description={`Жанр: ${t.genre || "не указан"}`}
                    />
                  </List.Item>
                )}
              />
            )}

          {paidNew.length > 0 && (
            <>
              <b style={{ fontSize: 24 }}>
                Предварительно: {amountForList(paidNew)} ₽
              </b><br/>
              <small style={{color:"#888", fontSize: 16}}>
                *окончательную сумму рассчитывает новый backend по актуальному каталогу
              </small><br/>

              <Button
                type="primary"
                danger
                icon={<ShoppingCartOutlined/>}
                style={{ marginTop: 12 }}
                onClick={()=>setPayM(true)}
              >
                Купить / подготовить
              </Button>
            </>
          )}
        </TabPane>

        {/* ❷ Уже купленные */}
        <TabPane tab={`Купленные (${paidOwned.length})`} key="owned">
          {paidOwned.length === 0
            ? <p>Нет купленных треков в корзине</p>
            : (
              <List
                dataSource={paidOwned}
                renderItem={t=>(
                  <List.Item>
                    <List.Item.Meta
                      avatar={<Avatar src={
                        t.track_cover_photo
                          ? `https://audiobuzz-web.tmweb.ru/uploads/thumbnails/media/${t.track_cover_photo}`
                          : "https://audiobuzz-web.tmweb.ru/uploads/thumbnails/default_cover.jpg"
                      }/>}
                      title={t.title}
                    />
                    <Button
                      type="link"
                      icon={<DeleteOutlined/>}
                      onClick={()=>removeFromQueue(t.id)}
                    />
                  </List.Item>
                )}
              />
            )}

          {paidOwned.length > 0 && (
            <Button
              type="primary"
              icon={<DownloadOutlined/>}
              style={{ marginTop: 12 }}
              onClick={()=>{ setZipM(true); prepareZip(paidOwned.map(t=>t.id), false); }}
            >
              Подготовить / скачать
            </Button>
          )}
        </TabPane>

        {/* ❸ Коран */}
        <TabPane tab={`Коран (${freeTracks.length})`} key="quran">
          {freeTracks.length === 0
            ? <p>Пусто</p>
            : (
              <List
                dataSource={freeTracks}
                renderItem={t=>(
                  <List.Item
                    actions={[
                      <Button
                        type="link"
                        icon={<DeleteOutlined/>}
                        onClick={()=>removeFromQueue(t.id)}
                      />
                    ]}
                  >
                    <List.Item.Meta
                      avatar={<Avatar src={
                        t.track_cover_photo
                          ? `https://audiobuzz-web.tmweb.ru/uploads/thumbnails/media/${t.track_cover_photo}`
                          : "https://audiobuzz-web.tmweb.ru/uploads/thumbnails/default_cover.jpg"
                      }/>}
                      title={t.title}
                    />
                  </List.Item>
                )}
              />
            )}

          {freeTracks.length > 0 && (
            <Button
              type="primary"
              icon={<DownloadOutlined/>}
              style={{ marginTop: 12 }}
              onClick={()=>{ setFreeM(true); prepareZip(freeTracks.map(t=>t.id), true); }}
            >
              Подготовить / скачать
            </Button>
          )}
        </TabPane>
      </Tabs>

      {/* ═════ Модалка «Коран» ═════ */}
      <Modal
        open={freeM}
        title="Скачать бесплатно (Коран)"
        footer={null}
        closable={zipSt!=="preparing"}
        onCancel={()=>zipSt!=="preparing" && setFreeM(false)}
      >
        <Radio.Group value={groupBy} onChange={e=>setGroupBy(e.target.value)}
                     style={{marginBottom:12}}>
          <Radio value="none">Все в один архив</Radio>
          <Radio value="artists">По артистам</Radio>
          <Radio value="albums">По альбомам</Radio>
        </Radio.Group>

        {zipSt==="preparing" && <Spin tip="Готовим архив…"/>}
        {zipSt==="ready"     && <Button onClick={startDownload}>Скачать</Button>}
      </Modal>

      {/* ═════ Модалка «Оплата» (не купленные) ═════ */}
      <Modal
        open={payM}
        title="Оплата / подготовка"
        footer={null}
        closable={zipSt!=="preparing"}
        onCancel={()=>zipSt!=="preparing" && setPayM(false)}
      >
        <p>Треков к оплате: {paidNew.length}</p>
        <p>Предварительная сумма: <b>{amountForList(paidNew)} ₽</b></p>

        <Radio.Group value={groupBy} onChange={e=>setGroupBy(e.target.value)}
                     style={{marginBottom:12}}>
          <Radio value="none">Все в один архив</Radio>
          <Radio value="artists">По артистам</Radio>
          <Radio value="albums">По альбомам</Radio>
        </Radio.Group>

        {/* сохранённые методы */}
        {methods.length>0 && (
          <>
            <Radio.Group
              value={payOpt}
              onChange={e=>setPayOpt(e.target.value)}
              style={{marginBottom:12,width:"100%"}}
            >
              {methods.slice(0, 1).map((m, index)=>{
                const lbl = /mir/i.test(m.card_type||"")      ? "Мир" :
                            /visa/i.test(m.card_type||"")     ? "Visa" :
                            /master/i.test(m.card_type||"")   ? "MasterCard" :
                            /yoo/i.test(m.card_type||"")||m.masked_pan?.startsWith("4100")
                                                               ? "YooMoney" : "Карта";
                const pan = m.masked_pan?.replace(/^\d+/,"••••").slice(-4);
                return (
                  <Radio key={`${m.masked_pan || "card"}-${index}`} value="saved"
                         style={{display:"block",padding:"4px 0"}}>
                    {lbl} {pan}
                  </Radio>
                );
              })}
              <Radio value="new" style={{display:"block",paddingTop:8}}>
                Другая карта / СБП
              </Radio>
            </Radio.Group>
            <Divider />
          </>
        )}

        {zipSt==="preparing" && <Spin tip="Готовим архив…"/>}
        {zipSt==="ready"     && <Button onClick={startDownload}>Скачать</Button>}
        {zipSt==="idle"      && (
          <Button type="primary"
                  onClick={pay}>
            Оплатить
          </Button>
        )}
      </Modal>

      {/* ═════ Модалка «Купленные» ═════ */}
      <Modal
        open={zipM}
        title="Скачать купленные"
        footer={null}
        closable={zipSt!=="preparing"}
        onCancel={()=>zipSt!=="preparing" && setZipM(false)}
      >
        <Radio.Group value={groupBy} onChange={e=>setGroupBy(e.target.value)}
                     style={{marginBottom:12}}>
          <Radio value="none">Все в один архив</Radio>
          <Radio value="artists">По артистам</Radio>
          <Radio value="albums">По альбомам</Radio>
        </Radio.Group>

        {zipSt==="preparing" && <Spin tip="Готовим архив…"/>}
        {zipSt==="ready"     && <Button onClick={startDownload}>Скачать</Button>}
      </Modal>
    </div>
  );
}
